import { supabase } from '../lib/supabase';
import { pushNotificationService } from './pushNotificationService';

export const supervisorAlertService = {
  // Get escalated alerts for supervisor
  async getEscalatedAlerts(supervisorId, filters = {}) {
    try {
      // Get supervisor's team members
      const { data: teamMembers } = await supabase
        ?.from('company_employees')?.select('user_id')?.eq('supervisor_id', supervisorId)?.eq('status', 'active');

      const teamUserIds = teamMembers?.map(m => m?.user_id) || [];

      // Fetch unacknowledged critical compliance alerts
      let complianceQuery = supabase
        ?.from('compliance_alerts')?.select(`*,created_by_profile:user_profiles!compliance_alerts_created_by_fkey(full_name, email),resolved_by_profile:user_profiles!compliance_alerts_resolved_by_fkey(full_name, email)`)?.in('severity', ['critical', 'high'])
        ?.eq('is_resolved', false)?.order('created_at', { ascending: false });

      // Fetch unacknowledged environmental alerts
      let environmentalQuery = supabase
        ?.from('environmental_alerts')?.select(`*,user:user_profiles!environmental_alerts_user_id_fkey(full_name, email),acknowledged_user:user_profiles!environmental_alerts_acknowledged_by_fkey(full_name, email)`)?.in('severity', ['critical', 'high'])
        ?.eq('is_acknowledged', false)?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.escalationLevel === 'unacknowledged_critical') {
        complianceQuery = complianceQuery?.eq('severity', 'critical');
        environmentalQuery = environmentalQuery?.eq('severity', 'critical');
      } else if (filters?.escalationLevel === 'overdue_response') {
        const overdueThreshold = new Date();
        overdueThreshold?.setHours(overdueThreshold?.getHours() - 2); // 2 hours overdue
        complianceQuery = complianceQuery?.lt('created_at', overdueThreshold?.toISOString());
        environmentalQuery = environmentalQuery?.lt('created_at', overdueThreshold?.toISOString());
      }

      const [complianceResult, environmentalResult] = await Promise.all([
        complianceQuery,
        environmentalQuery
      ]);

      // Process compliance alerts
      const complianceAlerts = (complianceResult?.data || [])?.map(alert => ({
        id: alert?.id,
        type: 'compliance',
        severity: alert?.severity,
        title: alert?.title,
        message: alert?.message,
        createdAt: alert?.created_at,
        dueDate: alert?.due_date,
        isResolved: alert?.is_resolved,
        createdBy: alert?.created_by,
        createdByProfile: alert?.created_by_profile,
        resolvedBy: alert?.resolved_by,
        resolvedByProfile: alert?.resolved_by_profile,
        metadata: alert?.metadata,
        escalationLevel: this.calculateEscalationLevel(alert?.created_at, alert?.severity),
        responseTime: this.calculateResponseTime(alert?.created_at, alert?.resolved_at)
      }));

      // Process environmental alerts
      const environmentalAlerts = (environmentalResult?.data || [])?.map(alert => ({
        id: alert?.id,
        type: 'environmental',severity: alert?.severity,title: alert?.alert_type?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase()),
        message: alert?.description || alert?.alert_type,
        createdAt: alert?.created_at,
        isAcknowledged: alert?.is_acknowledged,
        acknowledgedAt: alert?.acknowledged_at,
        acknowledgedBy: alert?.acknowledged_by,
        acknowledgedUser: alert?.acknowledged_user,
        userId: alert?.user_id,
        user: alert?.user,
        location: alert?.location,
        metadata: alert?.metadata,
        escalationLevel: this.calculateEscalationLevel(alert?.created_at, alert?.severity),
        responseTime: this.calculateResponseTime(alert?.created_at, alert?.acknowledged_at)
      }));

      // Combine and sort by escalation priority
      const allAlerts = [...complianceAlerts, ...environmentalAlerts];
      allAlerts?.sort((a, b) => {
        const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
        return priorityOrder?.[a?.severity] - priorityOrder?.[b?.severity] || 
               new Date(a?.createdAt) - new Date(b?.createdAt);
      });

      return allAlerts;
    } catch (error) {
      console.error('Error fetching escalated alerts:', error);
      throw error;
    }
  },

  // Calculate escalation level based on time and severity
  calculateEscalationLevel(createdAt, severity) {
    const now = new Date();
    const created = new Date(createdAt);
    const hoursElapsed = (now - created) / (1000 * 60 * 60);

    if (severity === 'critical' && hoursElapsed > 1) return 'urgent';
    if (severity === 'high' && hoursElapsed > 2) return 'urgent';
    if (hoursElapsed > 4) return 'overdue';
    if (hoursElapsed > 2) return 'escalated';
    return 'normal';
  },

  // Calculate response time in minutes
  calculateResponseTime(createdAt, resolvedAt) {
    if (!resolvedAt) return null;
    const created = new Date(createdAt);
    const resolved = new Date(resolvedAt);
    return Math.round((resolved - created) / (1000 * 60));
  },

  // Get team response time metrics
  async getTeamResponseMetrics(supervisorId) {
    try {
      // Get supervisor's team members
      const { data: teamMembers } = await supabase
        ?.from('company_employees')
        ?.select(`
          user_id,
          user_profile:user_profiles!company_employees_user_id_fkey(full_name, email)
        `)
        ?.eq('supervisor_id', supervisorId)
        ?.eq('status', 'active');

      const teamUserIds = teamMembers?.map(m => m?.user_id) || [];

      // Get response metrics for each team member
      const metricsPromises = teamUserIds?.map(async (userId) => {
        const member = teamMembers?.find(m => m?.user_id === userId);

        // Get resolved compliance alerts
        const { data: resolvedCompliance } = await supabase
          ?.from('compliance_alerts')
          ?.select('created_at, resolved_at')
          ?.eq('resolved_by', userId)
          ?.not('resolved_at', 'is', null)
          ?.gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString());

        // Get acknowledged environmental alerts
        const { data: acknowledgedEnvironmental } = await supabase
          ?.from('environmental_alerts')
          ?.select('created_at, acknowledged_at')
          ?.eq('acknowledged_by', userId)
          ?.not('acknowledged_at', 'is', null)
          ?.gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString());

        // Calculate average response times
        const complianceResponseTimes = (resolvedCompliance || [])?.map(alert => 
          this.calculateResponseTime(alert?.created_at, alert?.resolved_at)
        )?.filter(time => time !== null);

        const environmentalResponseTimes = (acknowledgedEnvironmental || [])?.map(alert => 
          this.calculateResponseTime(alert?.created_at, alert?.acknowledged_at)
        )?.filter(time => time !== null);

        const allResponseTimes = [...complianceResponseTimes, ...environmentalResponseTimes];
        const avgResponseTime = allResponseTimes?.length > 0 
          ? Math.round(allResponseTimes?.reduce((sum, time) => sum + time, 0) / allResponseTimes?.length)
          : 0;

        return {
          userId,
          fullName: member?.user_profile?.full_name,
          email: member?.user_profile?.email,
          totalResponses: allResponseTimes?.length,
          avgResponseTime,
          complianceResponses: complianceResponseTimes?.length,
          environmentalResponses: environmentalResponseTimes?.length
        };
      });

      const metrics = await Promise.all(metricsPromises);
      return metrics?.sort((a, b) => a?.avgResponseTime - b?.avgResponseTime);
    } catch (error) {
      console.error('Error fetching team response metrics:', error);
      throw error;
    }
  },

  // Get unacknowledged critical issues count
  async getUnacknowledgedCriticalCount(supervisorId) {
    try {
      const alerts = await this.getEscalatedAlerts(supervisorId, { 
        escalationLevel: 'unacknowledged_critical' 
      });
      return alerts?.length || 0;
    } catch (error) {
      console.error('Error fetching unacknowledged critical count:', error);
      return 0;
    }
  },

  // Reassign alert to another team member
  async reassignAlert(alertId, alertType, newAssigneeId, supervisorId, reason) {
    try {
      const reassignmentRecord = {
        alert_id: alertId,
        alert_type: alertType,
        reassigned_by: supervisorId,
        reassigned_to: newAssigneeId,
        reason,
        reassigned_at: new Date()?.toISOString()
      };

      // Send push notification to new assignee
      await pushNotificationService?.sendNotification({
        userIds: [newAssigneeId],
        type: 'alert_reassignment',
        title: 'Alert Reassigned to You',
        body: `You have been assigned an alert. Reason: ${reason}`,
        priority: 'high',
        requiresConfirmation: true,
        data: {
          alertId,
          alertType,
          reassignedBy: supervisorId,
          reason
        }
      });

      // Log the reassignment in audit log
      await supabase?.from('application_logs')?.insert({
        user_id: supervisorId,
        action: 'alert_reassignment',
        level: 'info',
        description: `Alert ${alertId} (${alertType}) reassigned to user ${newAssigneeId}. Reason: ${reason}`,
        metadata: reassignmentRecord
      });

      return { success: true, reassignment: reassignmentRecord };
    } catch (error) {
      console.error('Error reassigning alert:', error);
      throw error;
    }
  },

  // Escalate to management
  async escalateToManagement(alertId, alertType, supervisorId, escalationReason) {
    try {
      const escalationRecord = {
        alert_id: alertId,
        alert_type: alertType,
        escalated_by: supervisorId,
        escalation_level: 'management',
        reason: escalationReason,
        escalated_at: new Date()?.toISOString()
      };

      // Get management users
      const { data: managementUsers } = await supabase
        ?.from('user_profiles')
        ?.select('id')
        ?.in('role', ['manager', 'company_admin']);

      if (managementUsers && managementUsers?.length > 0) {
        const managementUserIds = managementUsers?.map(u => u?.id);

        // Send push notification to all management users
        await pushNotificationService?.sendNotification({
          userIds: managementUserIds,
          type: 'management_escalation',
          title: 'Alert Escalated to Management',
          body: `Alert ${alertId} escalated. Reason: ${escalationReason}`,
          priority: 'urgent',
          requiresConfirmation: true,
          data: {
            alertId,
            alertType,
            escalatedBy: supervisorId,
            reason: escalationReason
          }
        });
      }

      // Log the escalation
      await supabase?.from('application_logs')?.insert({
        user_id: supervisorId,
        action: 'alert_escalation_to_management',
        level: 'warning',
        description: `Alert ${alertId} (${alertType}) escalated to management. Reason: ${escalationReason}`,
        metadata: escalationRecord
      });

      return { success: true, escalation: escalationRecord };
    } catch (error) {
      console.error('Error escalating to management:', error);
      throw error;
    }
  },

  // Initiate disciplinary action
  async initiateDisciplinaryAction(userId, supervisorId, actionType, reason, alertId) {
    try {
      const disciplinaryRecord = {
        employee_id: userId,
        initiated_by: supervisorId,
        action_type: actionType,
        reason,
        related_alert_id: alertId,
        initiated_at: new Date()?.toISOString(),
        status: 'pending'
      };

      // Log the disciplinary action
      await supabase?.from('application_logs')?.insert({
        user_id: supervisorId,
        action: 'disciplinary_action_initiated',
        level: 'warning',
        description: `Disciplinary action (${actionType}) initiated for user ${userId}. Reason: ${reason}`,
        metadata: disciplinaryRecord
      });

      return { success: true, disciplinaryAction: disciplinaryRecord };
    } catch (error) {
      console.error('Error initiating disciplinary action:', error);
      throw error;
    }
  },

  // Generate performance improvement plan
  async generatePerformanceImprovementPlan(userId, supervisorId, issues, goals, timeline) {
    try {
      const pipRecord = {
        employee_id: userId,
        created_by: supervisorId,
        issues,
        goals,
        timeline,
        created_at: new Date()?.toISOString(),
        status: 'active'
      };

      // Log the PIP creation
      await supabase?.from('application_logs')?.insert({
        user_id: supervisorId,
        action: 'performance_improvement_plan_created',
        level: 'info',
        description: `Performance Improvement Plan created for user ${userId}`,
        metadata: pipRecord
      });

      return { success: true, pip: pipRecord };
    } catch (error) {
      console.error('Error generating performance improvement plan:', error);
      throw error;
    }
  },

  // Get enforcement action history
  async getEnforcementActionHistory(supervisorId, limit = 50) {
    try {
      const { data, error } = await supabase
        ?.from('application_logs')
        ?.select('*')
        ?.eq('user_id', supervisorId)
        ?.in('action', [
          'alert_reassignment',
          'alert_escalation_to_management',
          'disciplinary_action_initiated',
          'performance_improvement_plan_created'
        ])
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      if (error) throw error;

      return data?.map(log => ({
        id: log?.id,
        action: log?.action,
        description: log?.description,
        metadata: log?.metadata,
        createdAt: log?.created_at
      }));
    } catch (error) {
      console.error('Error fetching enforcement action history:', error);
      throw error;
    }
  },

  // Export supervisor report
  async exportSupervisorReport(supervisorId, dateRange) {
    try {
      const [alerts, metrics, enforcementHistory] = await Promise.all([
        this.getEscalatedAlerts(supervisorId),
        this.getTeamResponseMetrics(supervisorId),
        this.getEnforcementActionHistory(supervisorId)
      ]);

      const report = {
        generatedAt: new Date()?.toISOString(),
        supervisorId,
        dateRange,
        summary: {
          totalEscalatedAlerts: alerts?.length,
          criticalAlerts: alerts?.filter(a => a?.severity === 'critical')?.length,
          highAlerts: alerts?.filter(a => a?.severity === 'high')?.length,
          avgTeamResponseTime: metrics?.reduce((sum, m) => sum + m?.avgResponseTime, 0) / metrics?.length || 0,
          totalEnforcementActions: enforcementHistory?.length
        },
        escalatedAlerts: alerts,
        teamMetrics: metrics,
        enforcementActions: enforcementHistory
      };

      // Convert to CSV or JSON for download
      const reportJson = JSON.stringify(report, null, 2);
      const blob = new Blob([reportJson], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `supervisor-report-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
      link?.click();
      URL.revokeObjectURL(url);

      return report;
    } catch (error) {
      console.error('Error exporting supervisor report:', error);
      throw error;
    }
  }
};